import { useEffect, useMemo } from 'react'
import { z } from 'zod'
import { useExperiments } from '@mc/experiments-react'
import { trackError } from '@mc/monitoring'
import { useStaticData } from '@mc/static-data-react'
import { trackExperimentViewed } from '@mc/track-event'
import { isCampaignCertsValue } from '../prcng-1138_v1_certs_quiz_plans/constants'
import { useCertsQuizIntent } from '../prcng-1138_v1_certs_quiz_plans/hooks/useCertsQuizIntent'
import { type MCProBucket } from '../prcng-1194_v1_mc_pro_tier/constants'
import { MC_PRO_PROMO_FLAG_NAME } from '../prcng-1270_v1_mc_pro_promo/constants'
import { CM_DUAL_CTA_FLAG_NAME } from './constants'

// Highest priority first. A visitor without a configured `mcProBucket` on one
// flag falls through to the next, then to their own prcng-1194_v1 bucket.
// A gift reads only prcng-1270_v1, with no fallback.
export const MC_PRO_BUCKET_OVERRIDE_FLAGS = [
  CM_DUAL_CTA_FLAG_NAME,
  MC_PRO_PROMO_FLAG_NAME,
] as const

export type MCProBucketOverrideSource = {
  flag: string
  bucket: string | null | undefined
  variables: unknown
}

const VariablesSchema = z.object({
  mcProBucket: z
    .enum(['control', 'variant_1', 'variant_2', 'variant_3'])
    .nullish(),
})

const VARIANT_2_LAYOUTS = [
  'current',
  'premium_best_value',
  'pro_best_value',
] as const

export type Variant2Layout = (typeof VARIANT_2_LAYOUTS)[number]

const DEFAULT_VARIANT_2_LAYOUT: Variant2Layout = 'current'

const LayoutVariablesSchema = z.object({
  mcProVariant2Layout: z.enum(VARIANT_2_LAYOUTS).nullish(),
})

export const PRO_SLOT = {
  variant_1: 'family',
  variant_2: 'familyPlus',
  variant_3: 'family',
} as const

export const parseMCProBucketOverride = (
  bucket: string | null | undefined,
  variables: unknown,
  { isProPlan = false }: { isProPlan?: boolean } = {},
): { mcProBucket?: MCProBucket; isInvalid: boolean } => {
  if (!bucket) return { isInvalid: false }

  const parsed = VariablesSchema.safeParse(variables ?? {})

  // variant_3 shows MC Pro only with plan=pro (a Get Started in the CM page's MC Pro section); everyone else gets control.
  if (bucket === 'variant_3' && !isProPlan) {
    return { mcProBucket: 'control', isInvalid: !parsed.success }
  }

  return parsed.success
    ? { mcProBucket: parsed.data.mcProBucket ?? undefined, isInvalid: false }
    : { isInvalid: true }
}

export const resolveMCProBucketOverride = (
  sources: MCProBucketOverrideSource[],
  {
    isProPlan = false,
    isGift = false,
  }: { isProPlan?: boolean; isGift?: boolean } = {},
): {
  mcProBucket?: MCProBucket
  invalidSources: Omit<MCProBucketOverrideSource, 'bucket'>[]
} => {
  const eligibleSources = isGift
    ? sources.filter(({ flag }) => flag === MC_PRO_PROMO_FLAG_NAME)
    : sources
  const parsed = eligibleSources.map(({ flag, bucket, variables }) => ({
    flag,
    variables,
    ...parseMCProBucketOverride(bucket, variables, {
      // Only prcng-1266_v1's variant_3 waits for plan=pro.
      isProPlan: isProPlan || flag !== CM_DUAL_CTA_FLAG_NAME,
    }),
  }))

  return {
    mcProBucket: parsed.find(({ mcProBucket }) => mcProBucket)?.mcProBucket,
    invalidSources: parsed
      .filter(({ isInvalid }) => isInvalid)
      .map(({ flag, variables }) => ({ flag, variables })),
  }
}

export const parseVariant2Layout = (
  variables: unknown,
): { layout: Variant2Layout; isInvalid: boolean } => {
  const parsed = LayoutVariablesSchema.safeParse(variables ?? {})

  return parsed.success
    ? {
        layout: parsed.data.mcProVariant2Layout ?? DEFAULT_VARIANT_2_LAYOUT,
        isInvalid: false,
      }
    : { layout: DEFAULT_VARIANT_2_LAYOUT, isInvalid: true }
}

export const useMCProBucketOverride = ({
  bucket,
  isGift,
  reportInvalid = false,
  trackExposure = false,
}: {
  bucket: MCProBucket | null | undefined
  isGift: boolean
  reportInvalid?: boolean
  trackExposure?: boolean
}) => {
  const { experiments, experimentsVariables } = useExperiments()
  const variables = experimentsVariables?.[CM_DUAL_CTA_FLAG_NAME]
  const cmDualCtaBucket = experiments?.[CM_DUAL_CTA_FLAG_NAME]
  const { preselectedPlan } = useStaticData<{
    preselectedPlan?: 'pro' | null
  }>()
  const isProPlan = preselectedPlan === 'pro'
  const { mcProBucket, invalidSources } = useMemo(
    () =>
      resolveMCProBucketOverride(
        MC_PRO_BUCKET_OVERRIDE_FLAGS.map((flag) => ({
          flag,
          bucket: experiments?.[flag],
          variables: experimentsVariables?.[flag],
        })),
        { isProPlan, isGift },
      ),
    [experiments, experimentsVariables, isProPlan, isGift],
  )
  const { layout, isInvalid: isLayoutInvalid } = parseVariant2Layout(variables)
  const shouldReportInvalidLayout =
    reportInvalid && isLayoutInvalid && !!cmDualCtaBucket
  const { certsValue } = useCertsQuizIntent()

  useEffect(() => {
    if (!reportInvalid) return

    invalidSources.forEach(({ flag, variables: flagVariables }) => {
      trackError(new Error(`Invalid ${flag} mcProBucket variable`), {
        flag,
        variables: JSON.stringify(flagVariables),
      })
    })
  }, [reportInvalid, invalidSources])

  useEffect(() => {
    if (shouldReportInvalidLayout) {
      trackError(
        new Error('Invalid prcng-1266_v1 mcProVariant2Layout variable'),
        {
          flag: CM_DUAL_CTA_FLAG_NAME,
          variables: JSON.stringify(variables),
        },
      )
    }
  }, [shouldReportInvalidLayout, variables])

  // Visitors exposed on the CM page earlier in the session aren't counted
  // twice: track-event sends Experiment Viewed once per experiment per session.
  // A campaign visitor's override is discarded, so they never saw the arm.
  const isCampaign = isCampaignCertsValue(certsValue)
  const shouldTrackExposure =
    trackExposure && !isGift && !isCampaign && !!cmDualCtaBucket

  useEffect(() => {
    if (shouldTrackExposure) trackExperimentViewed(CM_DUAL_CTA_FLAG_NAME)
  }, [shouldTrackExposure])

  if (isGift) {
    return {
      bucket: mcProBucket,
      isOverridden: !!mcProBucket,
      variant2Layout: DEFAULT_VARIANT_2_LAYOUT,
    }
  }

  if (isCampaign) {
    return {
      bucket: 'control' as const,
      isOverridden: true,
      variant2Layout: DEFAULT_VARIANT_2_LAYOUT,
    }
  }

  const effectiveBucket = mcProBucket ?? bucket ?? undefined
  const variant2Layout =
    effectiveBucket === 'variant_2' ? layout : DEFAULT_VARIANT_2_LAYOUT

  if (!mcProBucket) {
    return { bucket: effectiveBucket, isOverridden: false, variant2Layout }
  }

  return {
    bucket: mcProBucket,
    isOverridden: true,
    variant2Layout,
    proSlot:
      preselectedPlan === 'pro' && mcProBucket !== 'control'
        ? PRO_SLOT[mcProBucket]
        : undefined,
  }
}
